import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import Logo from "../components/Logo";
import PaperOverlay from "../components/PaperOverlay";
import LangSwitcher from "../components/LangSwitcher";

export default function Login() {
  const { user, login, loading } = useAuth();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/dashboards" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) navigate("/dashboards");
    else setError(res.error);
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      {/* Brand visual side */}
      <div
        className="hidden lg:flex relative overflow-hidden p-10 flex-col justify-between"
        style={{
          backgroundColor: "hsl(var(--sidebar-bg))",
          backgroundImage: "url('/brand/pattern.png')",
          backgroundSize: "780px",
          backgroundBlendMode: "overlay",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--sidebar-bg))]/95 via-[hsl(var(--sidebar-bg))]/85 to-[hsl(var(--sidebar-active))]/40" />
        <div className="relative">
          <div className="flex items-center gap-3 text-white" data-testid="login-brand">
            <Logo
              className="h-10 w-10 logo-on-dark"
              color="white"
              accent="hsl(var(--sidebar-active))"
            />
            <div className="leading-tight">
              <div className="brand-text font-bold text-sm tracking-[0.22em] uppercase">Olimpia</div>
              <div className="brand-text font-semibold text-[10px] tracking-[0.36em] uppercase opacity-70">
                Sensor
              </div>
            </div>
          </div>
        </div>
        <div className="relative text-white max-w-md">
          <p className="kpi-label text-white/60 mb-4">{t("nav.tagline")}</p>
          <h1 className="text-3xl xl:text-4xl font-bold leading-tight tracking-tight">
            Olimpia Sensor
          </h1>
          <p className="text-white/75 mt-3 text-sm leading-relaxed">
            {t("auth.subtitle")}
          </p>
        </div>
        <div className="relative text-white/55 text-xs">© Olimpia Sensor {new Date().getFullYear()}</div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 brand-paper relative">
        <PaperOverlay opacity={0.4} />
        <div className="absolute top-4 right-4 z-10">
          <LangSwitcher />
        </div>
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6 fade-in" data-testid="login-form">
          <div className="space-y-2">
            <div className="flex items-center gap-3 lg:hidden mb-4">
              <Logo className="h-8 w-8" color="hsl(var(--primary))" accent="hsl(var(--primary))" />
              <div className="leading-tight">
                <div className="brand-text font-bold text-[12px] tracking-[0.22em] uppercase">Olimpia</div>
                <div className="brand-text font-semibold text-[10px] tracking-[0.32em] uppercase text-muted-foreground">
                  Sensor
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold tracking-tight">{t("auth.welcome")}</h2>
            <p className="text-sm text-muted-foreground">{t("auth.subtitle")}</p>
          </div>

          {error && (
            <div
              className="text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="kpi-label">{t("auth.email")}</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  data-testid="login-email"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="kpi-label">{t("auth.password")}</Label>
              <div className="relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="pw"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                  data-testid="login-password"
                />
              </div>
            </div>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full group"
            data-testid="login-submit"
          >
            {submitting ? t("auth.signing_in") : t("auth.sign_in")}
            <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </form>
      </div>
    </div>
  );
}
